/**
 * Overpass API Service (OpenStreetMap)
 * 
 * 100% FREE, unlimited queries
 * 8+ billion POI worldwide from community data
 * Best for: nature, viewpoints, heritage, unique places
 */

import { calculateDistance } from '@my-roadtrip/utils'
import { createLogger } from '../common/utils/logger.js'

const logger = createLogger('OverpassService')

const OVERPASS_API_URL = 'https://overpass-api.de/api/interpreter'
const REQUEST_TIMEOUT = 25000 // 25s timeout

export interface OverpassPOI {
  id: number
  type: 'node' | 'way' | 'relation'
  lat: number
  lon: number
  tags: Record<string, string>
}

export interface PlaceVerificationResult {
  matched: boolean
  confidence: number
  distanceMeters?: number
  name?: string
  location?: { coordinates: { lat: number; lng: number } }
  source: 'overpass'
  osmId?: number
  osmType?: string
  tags?: Record<string, string>
}

export interface PlaceResult {
  name: string
  description: string
  location: { coordinates: { lat: number; lng: number } }
  category: string
  rating?: number
  tags: string[]
  uniqueness: number
  osmId: number
  osmType: string
}

/**
 * Map app categories to OpenStreetMap tags
 */
const CATEGORY_TO_OSM_TAGS: Record<string, string[]> = {
  // Nature & Viewpoints
  viewpoint: ['tourism=viewpoint', 'natural=peak', 'natural=cliff'],
  scenic: ['tourism=viewpoint', 'natural=peak', 'natural=cliff', 'natural=beach'],
  nature: ['natural=waterfall', 'natural=beach', 'natural=cave', 'natural=spring', 'natural=hot_spring'],
  waterfall: ['natural=waterfall'],
  beach: ['natural=beach'],
  mountain: ['natural=peak', 'natural=volcano'],
  lake: ['natural=water', 'water=lake'],
  forest: ['natural=wood', 'landuse=forest'],
  
  // Heritage & Culture
  castle: ['historic=castle', 'historic=manor', 'historic=fort'],
  monument: ['historic=monument', 'historic=memorial', 'tourism=artwork'],
  museum: ['tourism=museum', 'tourism=gallery'],
  heritage: ['historic=castle', 'historic=monument', 'historic=ruins', 'historic=archaeological_site'],
  church: ['amenity=place_of_worship', 'building=church', 'building=cathedral'],
  ruins: ['historic=ruins', 'historic=archaeological_site'],
  
  // Food & Drink
  restaurant: ['amenity=restaurant', 'amenity=fast_food'],
  cafe: ['amenity=cafe', 'amenity=bar'],
  food: ['amenity=restaurant', 'amenity=cafe', 'amenity=fast_food'],
  
  // Services
  fuel: ['amenity=fuel'],
  parking: ['amenity=parking'],
  hotel: ['tourism=hotel', 'tourism=guest_house', 'tourism=hostel'],
  camping: ['tourism=camp_site', 'tourism=caravan_site'],
  
  // Activities
  hiking: ['route=hiking', 'tourism=information', 'information=guidepost'],
  attraction: ['tourism=attraction', 'tourism=theme_park'],
  entertainment: ['leisure=park', 'leisure=playground', 'tourism=zoo'],
  
  // Default fallback
  poi: ['tourism=attraction', 'tourism=viewpoint', 'historic=monument']
}

export class OverpassService {
  /**
   * Search for POIs near a location using Overpass API
   */
  async searchNearby(
    location: { lat: number; lng: number },
    categories: string[],
    radiusMeters: number = 10000
  ): Promise<PlaceResult[]> {
    try {
      // Build OSM tag filters
      const osmTags = this.categoriesToOsmTags(categories)
      
      if (osmTags.length === 0) {
        logger.warn('No OSM tags found for categories', { categories })
        return []
      }

      // Build Overpass QL query
      const query = this.buildOverpassQuery(location, radiusMeters, osmTags)
      
      logger.debug('Overpass query', { query: query.substring(0, 200) })

      // Execute query
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

      const response = await fetch(OVERPASS_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `data=${query}`,
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new Error(`Overpass API error: ${response.status} ${response.statusText}`)
      }

      const data = await response.json() as { elements: OverpassPOI[] }
      
      logger.info('Overpass results', { 
        count: data.elements.length,
        categories,
        radiusKm: (radiusMeters / 1000).toFixed(1)
      })

      // Transform to PlaceResult
      return this.transformResults(data.elements, location, categories)
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        logger.error('Overpass API timeout', { location, radiusMeters })
      } else {
        logger.error('Overpass search error', { error, location, categories })
      }
      return []
    }
  }

  /**
   * Verify a specific place exists in OSM
   */
  async verifyPlace(
    query: string,
    target: { lat: number; lng: number },
    options?: {
      proximity?: { lat: number; lng: number }
      maxDistanceMeters?: number
    }
  ): Promise<PlaceVerificationResult> {
    try {
      const proximity = options?.proximity ?? target
      const maxDistance = options?.maxDistanceMeters ?? 5000

      // Search around target location
      const query_osm = `
        [out:json][timeout:15];
        (
          node["name"~"${this.escapeRegex(query)}",i](around:${maxDistance},${proximity.lat},${proximity.lng});
          way["name"~"${this.escapeRegex(query)}",i](around:${maxDistance},${proximity.lat},${proximity.lng});
        );
        out center;
      `

      const response = await fetch(OVERPASS_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `data=${encodeURIComponent(query_osm)}`
      })

      if (!response.ok) {
        return { matched: false, confidence: 0, source: 'overpass' }
      }

      const data = await response.json() as { elements: OverpassPOI[] }

      if (data.elements.length === 0) {
        return { matched: false, confidence: 0, source: 'overpass' }
      }

      // Find closest match
      let bestMatch: OverpassPOI | null = null
      let minDistance = Infinity

      for (const element of data.elements) {
        const lat = element.lat || (element as any).center?.lat
        const lon = element.lon || (element as any).center?.lon
        
        if (!lat || !lon) continue

        const distance = calculateDistance(target, { lat, lng: lon })
        
        if (distance < minDistance) {
          minDistance = distance
          bestMatch = element
        }
      }

      if (!bestMatch) {
        return { matched: false, confidence: 0, source: 'overpass' }
      }

      const distanceMeters = minDistance
      const lat = bestMatch.lat || (bestMatch as any).center?.lat
      const lon = bestMatch.lon || (bestMatch as any).center?.lon

      // Calculate confidence (similar to Google/Mapbox)
      let confidence = 0
      
      if (distanceMeters < 100) confidence = 95
      else if (distanceMeters < 500) confidence = 85
      else if (distanceMeters < 1000) confidence = 70
      else if (distanceMeters < 2000) confidence = 55
      else confidence = 40

      // Boost if name matches well
      const osmName = bestMatch.tags.name || ''
      if (osmName.toLowerCase().includes(query.toLowerCase())) {
        confidence = Math.min(100, confidence + 10)
      }

      return {
        matched: confidence >= 50,
        confidence,
        distanceMeters,
        name: osmName,
        location: { coordinates: { lat, lng: lon } },
        source: 'overpass',
        osmId: bestMatch.id,
        osmType: bestMatch.type,
        tags: bestMatch.tags
      }
    } catch (error) {
      logger.error('Overpass verification error', { error, query })
      return { matched: false, confidence: 0, source: 'overpass' }
    }
  }

  /**
   * Map app categories to OSM tags
   */
  private categoriesToOsmTags(categories: string[]): string[] {
    const tags = new Set<string>()
    
    for (const category of categories) {
      const osmTags = CATEGORY_TO_OSM_TAGS[category.toLowerCase()] || CATEGORY_TO_OSM_TAGS.poi
      osmTags.forEach(tag => tags.add(tag))
    }
    
    return Array.from(tags)
  }

  /**
   * Build Overpass QL query
   */
  private buildOverpassQuery(
    location: { lat: number; lng: number },
    radiusMeters: number,
    osmTags: string[]
  ): string {
    // Build tag filters (e.g., tourism=viewpoint)
    const tagFilters = osmTags.map(tag => {
      const [key, value] = tag.split('=')
      return `node[${key}="${value}"](around:${radiusMeters},${location.lat},${location.lng});`
    }).join('\n  ')

    return `
[out:json][timeout:25];
(
  ${tagFilters}
);
out body;
>;
out skel qt;
    `.trim()
  }

  /**
   * Transform OSM elements to PlaceResult
   */
  private transformResults(
    elements: OverpassPOI[],
    anchor: { lat: number; lng: number },
    categories: string[]
  ): PlaceResult[] {
    const results: PlaceResult[] = []
    const seenNames = new Set<string>()

    for (const element of elements) {
      const name = element.tags.name || element.tags['name:en'] || element.tags['name:fr']
      
      if (!name || seenNames.has(name.toLowerCase())) continue
      seenNames.add(name.toLowerCase())

      const lat = element.lat || (element as any).center?.lat
      const lon = element.lon || (element as any).center?.lon
      
      if (!lat || !lon) continue

      // Calculate distance
      const distanceKm = calculateDistance(anchor, { lat, lng: lon }) / 1000

      // Generate description from tags
      const description = this.generateDescription(element.tags)

      // Calculate uniqueness based on tags
      const uniqueness = this.calculateUniqueness(element.tags)

      // Extract relevant tags
      const tags = this.extractTags(element.tags)

      // Determine category
      const category = this.determineCategory(element.tags, categories)

      results.push({
        name,
        description,
        location: { coordinates: { lat, lng: lon } },
        category,
        tags,
        uniqueness,
        osmId: element.id,
        osmType: element.type
      })
    }

    return results.slice(0, 20) // Preserve API order, limit to 20 results
  }

  /**
   * Generate description from OSM tags
   */
  private generateDescription(tags: Record<string, string>): string {
    const parts: string[] = []

    // Type
    if (tags.tourism) {
      const types: Record<string, string> = {
        viewpoint: 'Point de vue panoramique',
        attraction: 'Attraction touristique',
        museum: 'Musée',
        artwork: 'Œuvre d\'art',
        information: 'Point d\'information'
      }
      parts.push(types[tags.tourism] || 'Lieu touristique')
    }

    if (tags.natural) {
      const types: Record<string, string> = {
        peak: 'Sommet',
        waterfall: 'Cascade',
        beach: 'Plage',
        cave: 'Grotte',
        cliff: 'Falaise',
        spring: 'Source',
        hot_spring: 'Source thermale'
      }
      parts.push(types[tags.natural] || 'Site naturel')
    }

    if (tags.historic) {
      const types: Record<string, string> = {
        castle: 'Château historique',
        monument: 'Monument historique',
        ruins: 'Ruines historiques',
        memorial: 'Mémorial',
        archaeological_site: 'Site archéologique'
      }
      parts.push(types[tags.historic] || 'Site historique')
    }

    // Elevation
    if (tags.ele) {
      parts.push(`Altitude: ${tags.ele}m`)
    }

    // Wikipedia
    if (tags.wikipedia || tags.wikidata) {
      parts.push('Référencé sur Wikipedia')
    }

    // Description tag
    if (tags.description) {
      parts.push(tags.description.substring(0, 100))
    }

    return parts.join('. ') || 'Lieu d\'intérêt OpenStreetMap'
  }

  /**
   * Calculate uniqueness score (0-100)
   */
  private calculateUniqueness(tags: Record<string, string>): number {
    let score = 50 // Base score

    // Rare types are more unique
    if (tags.natural === 'waterfall') score += 20
    if (tags.natural === 'cave') score += 15
    if (tags.natural === 'hot_spring') score += 25
    if (tags.historic === 'castle') score += 15
    if (tags.historic === 'ruins') score += 10
    if (tags.tourism === 'viewpoint') score += 10

    // Has Wikipedia = notable
    if (tags.wikipedia || tags.wikidata) score += 15

    // Has elevation data = specific
    if (tags.ele) score += 5

    // Heritage classification
    if (tags['heritage'] || tags['heritage:operator']) score += 20

    return Math.min(100, score)
  }

  /**
   * Extract relevant tags for UI
   */
  private extractTags(tags: Record<string, string>): string[] {
    const result: string[] = []

    if (tags.tourism) result.push(tags.tourism)
    if (tags.natural) result.push(tags.natural)
    if (tags.historic) result.push(tags.historic)
    if (tags.amenity) result.push(tags.amenity)
    if (tags.ele) result.push(`${tags.ele}m`)
    if (tags.wikipedia) result.push('wikipedia')

    return result.slice(0, 5)
  }

  /**
   * Determine best matching category
   */
  private determineCategory(tags: Record<string, string>, requestedCategories: string[]): string {
    // Try to match requested categories
    for (const category of requestedCategories) {
      const osmTags = CATEGORY_TO_OSM_TAGS[category.toLowerCase()]
      if (osmTags) {
        for (const osmTag of osmTags) {
          const [key, value] = osmTag.split('=')
          if (tags[key] === value) return category
        }
      }
    }

    // Fallback to OSM tag-based category
    if (tags.tourism) return tags.tourism
    if (tags.natural) return 'nature'
    if (tags.historic) return 'heritage'
    if (tags.amenity) return tags.amenity

    return requestedCategories[0] || 'poi'
  }

  /**
   * Escape special regex characters for OSM query
   */
  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }
}
